import React, { useEffect, useState, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { useClub } from '../../hooks/useClub';

function monthLabel(iso) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}
function monthKey(iso) {
  return new Date(iso).toISOString().slice(0, 10);
}

function LeakageRow({ item, onResolve, updating }) {
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState(item.note || '');

  return (
    <div style={{
      background: 'var(--surface-2, #111)',
      border: `1px solid ${item.resolved ? 'var(--border, #222)' : '#ef444440'}`,
      borderRadius: 8, marginBottom: 8, padding: '10px 14px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text, #e5e5e5)', fontFamily: 'DM Sans, sans-serif' }}>
              {item.category}
            </span>
            <span style={{
              background: '#ffffff11', color: '#aaa', border: '1px solid #333',
              borderRadius: 4, padding: '2px 6px', fontSize: 10, whiteSpace: 'nowrap',
            }}>
              {monthLabel(item.periodMonth)}
            </span>
            <span style={{
              background: item.resolved ? '#16a34a22' : '#dc262622',
              color: item.resolved ? '#22c55e' : '#ef4444',
              border: `1px solid ${item.resolved ? '#22c55e' : '#ef4444'}`,
              borderRadius: 4, padding: '2px 7px', fontSize: 10, fontWeight: 700,
            }}>
              {item.resolved ? 'RESOLVED' : 'OPEN'}
            </span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {item.reversalCount} reversal{item.reversalCount !== 1 ? 's' : ''}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--red)', fontFamily: 'DM Mono, monospace' }}>
            ${Math.abs(item.totalReversed).toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </span>
          <button
            disabled={updating}
            onClick={() => onResolve(item.category, item.periodMonth, !item.resolved, note)}
            style={{
              background: item.resolved ? 'var(--surface-3, #1a1a1a)' : '#22c55e',
              color: item.resolved ? 'var(--text-muted)' : '#000',
              border: `1px solid ${item.resolved ? 'var(--border)' : '#22c55e'}`,
              borderRadius: 6, padding: '5px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer',
            }}
          >
            {item.resolved ? 'Reopen' : 'Mark Resolved'}
          </button>
          <button
            onClick={() => setNoteOpen(o => !o)}
            style={{
              background: 'var(--surface-3, #1a1a1a)', color: 'var(--text-muted)',
              border: '1px solid var(--border, #333)', borderRadius: 6,
              padding: '5px 10px', fontSize: 11, cursor: 'pointer',
            }}
          >
            {noteOpen ? '▲' : '📝'}
          </button>
        </div>
      </div>
      {noteOpen && (
        <div style={{ marginTop: 10, display: 'flex', gap: 6 }}>
          <input
            className="field-input"
            type="text"
            placeholder="Add a note..."
            value={note}
            onChange={e => setNote(e.target.value)}
            style={{ flex: 1, fontSize: 12 }}
          />
          <button
            disabled={updating}
            onClick={() => onResolve(item.category, item.periodMonth, item.resolved, note)}
            style={{
              background: 'var(--yellow)', color: '#000', border: 'none',
              borderRadius: 6, padding: '5px 14px', fontSize: 11, fontWeight: 700, cursor: 'pointer',
            }}
          >
            Save Note
          </button>
        </div>
      )}
      {item.note && !noteOpen && (
        <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>
          "{item.note}"
        </div>
      )}
    </div>
  );
}

export default function LeakageWatchlist() {
  const { get, post } = useApi();
  const { currentClub } = useClub();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('open'); // open | all
  const [updating, setUpdating] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    get(`/revenue/leakage-watchlist?club=${currentClub.id}`)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [currentClub.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  async function handleResolve(category, periodMonth, resolved, note) {
    const key = `${category}|${periodMonth}`;
    setUpdating(prev => ({ ...prev, [key]: true }));
    try {
      await post('/revenue/leakage-watchlist/resolve', {
        locationId: currentClub.id, category, periodMonth: monthKey(periodMonth), resolved, note,
      });
      setData(prev => prev ? ({
        ...prev,
        categories: prev.categories.map(c =>
          c.category === category && c.periodMonth === periodMonth ? { ...c, resolved, note } : c
        ),
      }) : prev);
    } catch (e) {
      console.error('Leakage resolve failed', e);
    } finally {
      setUpdating(prev => ({ ...prev, [key]: false }));
    }
  }

  if (loading) return <div className="empty-state"><div className="spinner" /></div>;
  if (error) return (
    <div style={{ padding: 24, color: 'var(--red)', textAlign: 'center' }}>
      Failed to load Leakage Watchlist — {error}
      <button onClick={load} style={{ marginLeft: 12, background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '4px 12px', borderRadius: 6, cursor: 'pointer' }}>
        Retry
      </button>
    </div>
  );

  const all = data?.categories || [];
  // Only true reversal/refund categories are actionable leakage items — positive-amount
  // rows (the base category itself) come back from the same query but aren't reversals.
  const reversalsOnly = all.filter(c => c.category?.toLowerCase().includes('reversal'));
  const filtered = filter === 'open' ? reversalsOnly.filter(c => !c.resolved) : reversalsOnly;
  const openCount = reversalsOnly.filter(c => !c.resolved).length;
  const totalOpen = reversalsOnly.filter(c => !c.resolved).reduce((sum, c) => sum + Math.abs(c.totalReversed), 0);

  return (
    <div style={{ padding: '16px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          ⚠️ <strong style={{ color: 'var(--red)' }}>{openCount}</strong> open ·{' '}
          <strong style={{ color: 'var(--red)', fontFamily: 'DM Mono, monospace' }}>
            ${totalOpen.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </strong> unresolved
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {['open', 'all'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              style={{
                background: filter === f ? 'var(--yellow)' : 'var(--surface-2)',
                color: filter === f ? '#000' : 'var(--text-muted)',
                border: '1px solid var(--border)', borderRadius: 6,
                padding: '4px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize',
              }}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: 32, fontStyle: 'italic' }}>
          No {filter === 'open' ? 'open' : ''} leakage items.
        </div>
      ) : (
        filtered.map(item => (
          <LeakageRow
            key={`${item.category}|${item.periodMonth}`}
            item={item}
            onResolve={handleResolve}
            updating={updating[`${item.category}|${item.periodMonth}`]}
          />
        ))
      )}
    </div>
  );
}
